import { SUMMARISING_OUTPUT_TYPE } from "../types/constants";
import { getErrorMessage, isFetchBaseQueryError } from "../types/types";
import { summaryFingerprint } from "../utils/reportResultsUtils";
import { DASHBOARD_REPORT_ID } from "../utils/submissionStatusSpec";
import { useEnhancedColumns } from "./useEnhancedColumns";
import { useReportExportContext } from "./useReportExportContext";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import * as reportsV3Service from "services/reportsV3Service";

export const useReportExecution = (props) => {
  const isSavedReportMode = "reportId" in props && !!props.reportId;
  const reportId = isSavedReportMode ? props.reportId : undefined;
  const isDashboardReport = reportId === DASHBOARD_REPORT_ID;
  const immediateSpecification = !isSavedReportMode
    ? props.specification
    : undefined;
  const displayName = props.displayName || "Unknown Report";
  const description = props.description;

  const [results, setResults] = useState(null);
  const [selectedResultIndex, setSelectedResultIndex] = useState(0);
  const [summaryResults, setSummaryResults] = useState(null);
  const [error, setError] = useState(null);
  const [summaryError, setSummaryError] = useState(null);
  const [isRerunning, setIsRerunning] = useState(false);

  const executingRef = useRef(false);
  const identityRef = useRef(undefined);
  const lastSpecFingerprintRef = useRef(null);
  const executionRunRef = useRef(0);
  const summaryRunRef = useRef(0);
  const summaryFingerprintRef = useRef(null);

  // Optional "as at" date lifted into ReportExportContext (set via the field
  // beside the Run button). Held in a ref so every execution path — initial
  // load, auto-rebuild after a save, etc. — picks up the current value without
  // adding it to the effect's dependency list (which would auto-re-run the
  // report the moment the field changes, before the user presses Run).
  const { asAt } = useReportExportContext() ?? {};
  const asAtRef = useRef(asAt);
  asAtRef.current = asAt;

  // The SUMMARISING run only feeds the filter value lists, so it waits until
  // a filter is first opened (requestSummary). Until then the latest summary
  // request is held here instead of being sent.
  const summaryRequestedRef = useRef(false);
  const deferredSummaryRef = useRef(null);

  const {
    currentData: savedReport,
    isLoading: savedReportLoading,
    error: savedReportError,
  } = reportsV3Service.useGetReportQuery(reportId, {
    skip: !isSavedReportMode,
  });

  const savedReportRef = useRef(savedReport);
  savedReportRef.current = savedReport;

  const immediateReport = useMemo(() => {
    if (isSavedReportMode || !immediateSpecification) return undefined;
    return {
      internalName: "",
      displayName: displayName,
      description: description,
      type: "SINGLE_TABLE_REPORT",
      specification: immediateSpecification,
    };
  }, [isSavedReportMode, immediateSpecification, displayName, description]);

  const report = isSavedReportMode ? savedReport : immediateReport;
  const reportLoading = isSavedReportMode
    ? savedReportLoading || (!savedReport && !savedReportError)
    : false;

  const reportError =
    isSavedReportMode && !reportLoading
      ? savedReportError
      : summaryError
      ? summaryError
      : undefined;

  const {
    columns: enhancedColumns,
    setQuerySpec,
    setResultColumns,
    applyColumnRenames,
    reorderColumns,
  } = useEnhancedColumns();

  const [
    executeReport,
    { isLoading: isExecutingSaved },
  ] = reportsV3Service.useExecuteReportMutation();

  const [
    runImmediateReport,
    { isLoading: isExecutingImmediate },
  ] = reportsV3Service.useRunImmediateReportMutation();

  const [
    executeSummaryReport,
    { isLoading: isLoadingExecuteSummary },
  ] = reportsV3Service.useExecuteReportMutation();

  const [
    runImmediateSummary,
    { isLoading: isLoadingImmediateSummary },
  ] = reportsV3Service.useRunImmediateReportMutation();

  const isSummaryLoading = isLoadingExecuteSummary || isLoadingImmediateSummary;
  const isExecuting = isExecutingImmediate || isExecutingSaved;

  const deriveQuerySpecFromColumns = useCallback(
    (columns) =>
      columns.map((col) => ({
        column: col.internalName,
        displayName: col.displayName,
      })),
    []
  );

  const handleResult = useCallback(
    (data) => {
      setResults(data);
      if (data?.[0]?.columns?.length) {
        setQuerySpec(deriveQuerySpecFromColumns(data[0].columns));
        setResultColumns(data[0].columns);
        lastSpecFingerprintRef.current = data[0].columns
          .map(
            (c) =>
              `${c.internalName}|${c.displayName ?? ""}|${
                c.notForExport ? 1 : 0
              }|${c.pinned ? 1 : 0}`
          )
          .join(",");
      }
    },
    [setResults, setQuerySpec, setResultColumns, deriveQuerySpecFromColumns]
  );

  useEffect(() => {
    if (!results || results.length <= 1) return;
    const selectedResult = results[selectedResultIndex];
    if (!selectedResult?.columns?.length) return;
    setQuerySpec(deriveQuerySpecFromColumns(selectedResult.columns));
    setResultColumns(selectedResult.columns);
  }, [
    selectedResultIndex,
    results,
    setQuerySpec,
    setResultColumns,
    deriveQuerySpecFromColumns,
  ]);

  const handleSummaryResult = useCallback(
    (data) => {
      if (data && data.length > 0) {
        const summary = data[0]?.tableSummary;
        setSummaryResults(summary);
      }
    },
    [setSummaryResults]
  );

  useEffect(() => {
    const identity = isSavedReportMode ? reportId : immediateSpecification;

    if (identityRef.current !== identity) {
      identityRef.current = identity;
      executingRef.current = false;
      lastSpecFingerprintRef.current = null;
      summaryRequestedRef.current = false;
      deferredSummaryRef.current = null;
      setResults(null);
      setSummaryResults(null);
      setError(null);
      setSelectedResultIndex(0);
    }

    const currentFingerprint = isSavedReportMode
      ? (savedReport?.specification?.outputColumns ?? [])
          .map(
            (c) =>
              `${c.column}|${c.displayName ?? ""}|${c.notForExport ? 1 : 0}|${
                c.pinned ? 1 : 0
              }`
          )
          .join(",")
      : "";

    if (isSavedReportMode && executingRef.current) {
      if (
        lastSpecFingerprintRef.current !== null &&
        currentFingerprint !== lastSpecFingerprintRef.current
      ) {
        executingRef.current = false;
      }
    }

    const savedNonDashboard = isSavedReportMode && !isDashboardReport;
    if (savedNonDashboard && !reportId) return;
    if (!savedNonDashboard && (!report || reportLoading)) return;
    if (executingRef.current) return;

    const thisRun = ++executionRunRef.current;
    executingRef.current = true;

    const useImmediate = isDashboardReport || !isSavedReportMode;
    const reportToRun = useImmediate
      ? {
          type: report.type ?? "SINGLE_TABLE_REPORT",
          displayName: report.displayName ?? "Untitled Report",
          description: report.description ?? "",
          specification: report.specification,
        }
      : null;

    const asAtArg = asAtRef.current || undefined;

    const getImmediateData = async () => {
      return runImmediateReport({
        report: reportToRun,
        outputType: "JSON",
        ...(asAtArg ? { asAt: asAtArg } : {}),
      }).unwrap();
    };

    const getSavedReportData = async () => {
      return executeReport({
        reportId,
        outputType: "JSON",
        ...(asAtArg ? { asAt: asAtArg } : {}),
      }).unwrap();
    };

    const getImmediateSummary = async () => {
      return await runImmediateSummary({
        report: reportToRun,
        outputType: SUMMARISING_OUTPUT_TYPE,
        ...(asAtArg ? { asAt: asAtArg } : {}),
      }).unwrap();
    };

    const getSavedReportSummary = async () => {
      return await executeSummaryReport({
        reportId,
        outputType: SUMMARISING_OUTPUT_TYPE,
        ...(asAtArg ? { asAt: asAtArg } : {}),
      }).unwrap();
    };

    const startReport = async () => {
      setError(null);
      try {
        const data = useImmediate
          ? await getImmediateData()
          : await getSavedReportData();
        if (thisRun !== executionRunRef.current) return;
        if (data) {
          handleResult(data);
        }
      } catch (caughtError) {
        if (thisRun !== executionRunRef.current) return;
        const errorMessage = isFetchBaseQueryError(caughtError)
          ? getErrorMessage(caughtError)
          : caughtError instanceof Error
          ? caughtError.message
          : "Failed to execute report";
        setError(errorMessage);
      }
    };
    const startSummary = async () => {
      setSummaryError(null);
      const thisSummaryRun = ++summaryRunRef.current;
      summaryFingerprintRef.current = useImmediate
        ? summaryFingerprint(reportToRun, asAtArg)
        : null;
      try {
        const data = useImmediate
          ? await getImmediateSummary()
          : await getSavedReportSummary();
        if (thisSummaryRun !== summaryRunRef.current) return;
        if (data) {
          handleSummaryResult(data);
        }
      } catch (caughtError) {
        if (thisSummaryRun !== summaryRunRef.current) return;
        const errorMessage = isFetchBaseQueryError(caughtError)
          ? getErrorMessage(caughtError)
          : caughtError instanceof Error
          ? caughtError.message
          : "Failed to execute report";
        setSummaryError(errorMessage);
      }
    };
    void startReport();
    if (summaryRequestedRef.current) {
      void startSummary();
    } else {
      deferredSummaryRef.current = startSummary;
    }
  }, [
    isSavedReportMode,
    isDashboardReport,
    reportId,
    immediateSpecification,
    savedReport,
    executeReport,
    runImmediateReport,
    handleResult,
    reportLoading,
    report,
    runImmediateSummary,
    executeSummaryReport,
    handleSummaryResult,
  ]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Re-requests the summary for a rerun whose summary-relevant parts changed.
  const fetchSummary = useCallback(
    async (modifiedReport, asAt) => {
      const fingerprint = summaryFingerprint(modifiedReport, asAt);
      if (fingerprint === summaryFingerprintRef.current) return;
      summaryFingerprintRef.current = fingerprint;
      const thisSummaryRun = ++summaryRunRef.current;
      setSummaryError(null);
      try {
        const data = await runImmediateSummary({
          report: modifiedReport,
          outputType: SUMMARISING_OUTPUT_TYPE,
          ...(asAt ? { asAt } : {}),
        }).unwrap();
        if (thisSummaryRun !== summaryRunRef.current) return;
        if (data) {
          handleSummaryResult(data);
        }
      } catch (caughtError) {
        if (thisSummaryRun !== summaryRunRef.current) return;
        summaryFingerprintRef.current = null;
        setSummaryError(
          isFetchBaseQueryError(caughtError)
            ? getErrorMessage(caughtError)
            : caughtError instanceof Error
            ? caughtError.message
            : "Failed to execute report"
        );
      }
    },
    [runImmediateSummary, handleSummaryResult]
  );

  const refreshSummary = useCallback(
    (modifiedReport, asAt) => {
      if (!summaryRequestedRef.current) {
        deferredSummaryRef.current = () => fetchSummary(modifiedReport, asAt);
        return;
      }
      return fetchSummary(modifiedReport, asAt);
    },
    [fetchSummary]
  );

  // Called when a filter is first opened: sends the held summary request, if
  // any. Later reruns then refresh the summary as they go.
  const requestSummary = useCallback(() => {
    if (summaryRequestedRef.current) return;
    summaryRequestedRef.current = true;
    const deferred = deferredSummaryRef.current;
    deferredSummaryRef.current = null;
    if (deferred) void deferred();
  }, []);

  const rerun = useCallback(
    async (specOverrides, asAt) => {
      if (!report) return;
      setIsRerunning(true);
      setError(null);
      try {
        let modifiedReport;
        if (specOverrides != null && "reports" in specOverrides) {
          modifiedReport = specOverrides;
        } else {
          modifiedReport = {
            type: report.type,
            displayName: report.displayName,
            description: report.description,
            specification: {
              ...report.specification,
              ...specOverrides,
            },
          };
        }
        void refreshSummary(modifiedReport, asAt);
        const data = await runImmediateReport({
          report: modifiedReport,
          outputType: "JSON",
          ...(asAt ? { asAt } : {}),
        }).unwrap();
        if (data) {
          setResults(data);
          setSelectedResultIndex((prev) => Math.min(prev, data.length - 1));
          if (data[0]?.columns?.length) {
            setQuerySpec(deriveQuerySpecFromColumns(data[0].columns));
            setResultColumns(data[0].columns);
          }
        }
      } catch (caughtError) {
        const errorMessage = isFetchBaseQueryError(caughtError)
          ? getErrorMessage(caughtError)
          : caughtError instanceof Error
          ? caughtError.message
          : "Failed to re-run report";
        setResults(null);
        setError(errorMessage);
      } finally {
        setIsRerunning(false);
      }
    },
    [
      report,
      runImmediateReport,
      refreshSummary,
      setQuerySpec,
      setResultColumns,
      deriveQuerySpecFromColumns,
    ]
  );

  const displayError =
    reportError !== undefined ? "Failed to load report." : error;
  return {
    reportData: results?.[selectedResultIndex] ?? null,
    allResults: results,
    selectedResultIndex,
    setSelectedResultIndex,
    requestSummary,
    error: displayError,
    isLoading: reportLoading,
    isSummaryLoading,
    summaryResults,
    isExecuting,
    isRerunning,
    report,
    enhancedColumns,
    reorderColumns,
    applyColumnRenames,
    setResults,
    clearError,
    rerun,
  };
};
